import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { usersService, type InviteUserPayload, type UsersListResponse } from '../services/users.service';
import { getQueryState, type QueryWithState } from './useQueryWithState';

export const useUsers = (params: URLSearchParams): QueryWithState<UsersListResponse> => {
  const query = useQuery({
    queryKey: ['users', params.toString()],
    queryFn: () => usersService.getList(params),
    placeholderData: keepPreviousData,
  });

  return {
    ...query,
    renderState: getQueryState(query, (data) => data.data.length === 0),
  };
};

export const useUser = (id: string | number, enabled = true): QueryWithState<unknown> => {
  const query = useQuery({
    queryKey: ['users', id],
    queryFn: () => usersService.getById(id),
    enabled,
  });

  return {
    ...query,
    renderState: getQueryState(query),
  };
};

export const useSaveUser = () =>
  {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: ({ id, payload }: { id: string | number | null; payload: Record<string, unknown> }) =>
        usersService.save(id, payload),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['users'] });
      },
    });
  };

export const useInviteUser = () =>
  useMutation({
    mutationFn: (payload: InviteUserPayload) => usersService.invite(payload),
  });

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => usersService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useApproveUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => usersService.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useRejectUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => usersService.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useApproveInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => usersService.approveInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};

export const useRejectInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string | number) => usersService.rejectInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
};
